import {
  FilterList as FilterListIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import {
  Box,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { UploadCaptureLine } from "../../../types/interfaces/file/imports/captures";
import { UploadCaptureForm } from "./UploadCaptureForm";

interface RowProps {
  line: UploadCaptureLine;
  open: boolean;
  onToggle: () => void;
}

const Row = ({ line, open, onToggle }: RowProps) => {
  const theme = useTheme();
  const preview = line.preview;
  if (!preview) return null;

  return (
    <React.Fragment>
      <TableRow
        onClick={onToggle}
        sx={{ cursor: "pointer", "& > *": { borderBottom: "unset" } }}
      >
        <TableCell>
          {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        </TableCell>
        <TableCell component="th" scope="row">
          {preview.name}
        </TableCell>
        <TableCell>{preview.entityName?.toUpperCase()}</TableCell>
        <TableCell>{preview.managementUnitId}</TableCell>
      </TableRow>
      <TableRow
        sx={open ? { backgroundColor: theme.palette.action.hover } : {}}
      >
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <UploadCaptureForm capture={preview} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

interface UploadCapturesPreviewTableProps {
  lines: UploadCaptureLine[];
}

export function UploadCapturesPreviewTable({
  lines,
}: Readonly<UploadCapturesPreviewTableProps>) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [filter, setFilter] = useState("");
  const [openRowId, setOpenRowId] = useState<number | null | string>(null);

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value);
    setPage(0);
  };

  const filteredData = lines.filter(
    (input) =>
      input.preview?.name?.toLowerCase().includes(filter.toLowerCase()) ||
      input.preview?.entityName?.toLowerCase().includes(filter.toLowerCase()) ||
      input.preview?.managementUnitName
        ?.toLowerCase()
        .includes(filter.toLowerCase()),
  );

  const visibleRows = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Paper sx={{ p: 2 }}>
      <Toolbar>
        <Typography sx={{ flex: "1 1 100%" }} variant="h5">
          {t("imports.confirm-dialog.data-table-title")}
        </Typography>

        <Tooltip title={t("generic.filter")}>
          <IconButton onClick={() => setShowFilters(!showFilters)}>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      </Toolbar>

      {showFilters && (
        <Grid2 container spacing={3} sx={{ p: 2 }}>
          <Grid2 size={12}>
            <TextField
              name="name"
              label={t("generic.search")}
              value={filter}
              onChange={handleFilterChange}
              fullWidth
            />
          </Grid2>
        </Grid2>
      )}

      <TableContainer>
        <Table aria-label="collapsible table" sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "5%" }} />
              <TableCell>
                {t("captures.capture.general.identification.name")}
              </TableCell>
              <TableCell>
                {t("captures.capture.general.identification.entity")}
              </TableCell>
              <TableCell>
                {t("captures.capture.general.identification.management-unit")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((line) => (
              <Row
                key={line.line}
                line={line}
                open={openRowId === line.line}
                onToggle={() =>
                  setOpenRowId(openRowId === line.line ? null : line.line)
                }
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(Number.parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </Paper>
  );
}
